/**
 * Item Location Balance Service
 *
 * Service for fetching item-bin balance data from the DataInspectionController.
 * Uses the existing ItemBins endpoints to retrieve location balances.
 */

import { AuthService } from './authService';
import { ItemLocationBalanceDto } from '../types/itemLocationBalance';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/** Respons fra retting av serienummer (POST /itembins/{id}/correct-serial) */
export interface CorrectSerialNoResponse {
  success: boolean;
  itemBinsId: number;
  itemNo: string;
  binCode: string;
  oldSerialNo: string;
  newSerialNo: string;
  errorMessage?: string;
}

/** Respons fra retting av batch/utløpsdato (POST /itembins/{id}/correct-batch) */
export interface CorrectBatchResponse {
  success: boolean;
  itemBinsId: number;
  itemNo: string;
  binCode: string;
  oldBatchNo?: string;
  newBatchNo?: string;
  oldExpiryDate?: string;
  newExpiryDate?: string;
  errorMessage?: string;
}

class ItemLocationBalanceService {
  /**
   * Fetches all item-bin relationships with balances
   * @returns Promise<ItemLocationBalanceDto[]> Array of all item bins
   */
  async getAllItemBins(): Promise<ItemLocationBalanceDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/data-inspection/itembins`
    );

    if (!response.ok) {
      throw new Error(`Failed to get item bins: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Fetches all locations for a specific item
   * @param itemNo - The item number to filter by
   * @returns Promise<ItemLocationBalanceDto[]> Array of item bins for the specified item
   */
  async getItemBinsByItemNo(itemNo: string): Promise<ItemLocationBalanceDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/data-inspection/itembins/item/${encodeURIComponent(itemNo)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get item bins for item ${itemNo}: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Fetches all items stored in a specific bin location
   * @param binCode - The bin code to filter by
   * @returns Promise<ItemLocationBalanceDto[]> Array of item bins for the specified location
   */
  async getItemBinsByBinCode(binCode: string): Promise<ItemLocationBalanceDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/data-inspection/itembins/by-bin/${encodeURIComponent(binCode)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get item bins for bin ${binCode}: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Rett et feilregistrert serienummer på en ItemBins-rad (kun lagersjef/administrator).
   * Ren rename — saldo og lokasjon røres ikke; korreksjonspar (SERIEFIX) skrives i historikken.
   */
  async correctSerialNo(itemBinsId: number, oldSerialNo: string, newSerialNo: string): Promise<CorrectSerialNoResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/itembins/${itemBinsId}/correct-serial`,
      { oldSerialNo, newSerialNo }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Kunne ikke rette serienummer'));
    }

    return await response.json();
  }

  /**
   * Rett batchnummer og/eller utløpsdato på en ItemBins-rad (kun lagersjef/administrator,
   * varetype 1/4/5). Ren re-merking — saldo og lokasjon røres ikke; korreksjonspar (BATCHFIX)
   * skrives i historikken. Nye verdier er komplett ny tilstand (null/tom = fjern).
   */
  async correctBatch(
    itemBinsId: number,
    oldBatchNo: string | null,
    newBatchNo: string | null,
    newExpiryDate: string | null
  ): Promise<CorrectBatchResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/itembins/${itemBinsId}/correct-batch`,
      { oldBatchNo, newBatchNo, newExpiryDate }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Kunne ikke rette batch/utløpsdato'));
    }

    return await response.json();
  }
}

export const itemLocationBalanceService = new ItemLocationBalanceService();
